import * as THREE from 'three';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import { BaseEnemy } from './base.js';
import { prepareModel } from '../../engine/loader.js';
import { resolveCircleWalls } from '../../engine/collision.js';

// Boss: uses FBX model if available, procedural fallback otherwise.
// Two phases selectable via `phase` (1 = miniboss room 5, 2 = final room 10).

const PHASE_STATS = {
  1: {
    hp: 24, moveSpeed: 2.8, sightRange: 40, attackRange: 22,
    fireCooldown: 1.6, projSpeed: 12, projDamage: 1, radius: 1.1, height: 2.6,
    moves: ['single', 'fan3', 'slash'],
  },
  2: {
    hp: 63, moveSpeed: 3.4, sightRange: 40, attackRange: 24,
    fireCooldown: 1.2, projSpeed: 14, projDamage: 2, radius: 1.2, height: 2.8,
    moves: ['single', 'fan5', 'spiral', 'chargeBurst', 'slash'],
  },
};

// Ranged patterns get a short pre-fire "aim" telegraph so the bow/slash reads clearly.
const RANGED_PATTERNS = new Set(['single', 'fan3', 'fan5', 'spiral', 'chargeBurst']);
const AIM_TELEGRAPH = 0.35;
const SLASH_RANGE = 2.2;
const SLASH_CONNECT_RANGE = 2.6;
const JUMP_BACK_TRIGGER = 3.0;
const JUMP_BACK_DISTANCE = 5.0;
const JUMP_BACK_COOLDOWN = 6.0;

export class Boss extends BaseEnemy {
  constructor({ scene, x, z, audio, fbx, phase = 1 }) {
    const phaseData = PHASE_STATS[phase] || PHASE_STATS[1];
    super({
      scene, x, z, audio,
      stats: {
        hp: phaseData.hp,
        moveSpeed: phaseData.moveSpeed,
        sightRange: phaseData.sightRange,
        attackRange: phaseData.attackRange,
        fireCooldown: phaseData.fireCooldown,
        projectileSpeed: phaseData.projSpeed,
        projectileDamage: phaseData.projDamage,
        radius: phaseData.radius,
        height: phaseData.height,
        bodyColor: 0x1a0a18,
        accentColor: 0xff2d6b,
      },
    });

    this.isBoss = true;
    this.phase = phase;
    this.moves = phaseData.moves;
    this.enraged = false;
    this.fbx = fbx;
    this.mixer = null;
    this._currentAction = null;
    this._actions = {};
    this._idleAction = null;
    this._walkAction = null;
    this._aimAction = null;
    this._jumpAction = null;
    this._slashAction = null;
    this._chargeAction = null;
    this._powerAction = null;
    this._moveIndex = 0;
    this._movePattern = null;
    this._moveStep = 0;
    this._stepTimer = 0;
    this._aimDelay = 0;

    this._bossState = 'idle';
    this._jumpCd = 0;
    this._jumpActive = false;
    this._jumpT = 0;
    this._jumpDur = 0;
    this._jumpFromX = 0; this._jumpFromZ = 0;
    this._jumpToX = 0; this._jumpToZ = 0;

    this._slashActive = false;
    this._slashDealt = false;
    this._pendingMelee = -1;

    this._powerupActive = false;
    this.usingFbxVisual = false;
    this._lastHpRatio = 1;
    this._phase70Triggered = false;
    this._phase30Triggered = false;
    this._phase2CardTriggered = false;
    this._isSummoningHidden = false;
    this._activeSummonAdds = [];
    this._pendingSummonDrops = 0;
    this._activeSummonWaveToken = null;
    this._summonReturnX = null;
    this._summonReturnZ = null;
    this._hiddenRadius = null;
    this._summonParkX = null;
    this._summonParkZ = null;

    // Inertial physics velocity (persists across frames; distinct from base.vx/vz which are frame-local)
    this._physVx = 0;
    this._physVz = 0;

    // Visual Y effects: jump arc parabola, walk bob, landing squash spring
    this._jumpY = 0;
    this._jumpArcT = 0;
    this._bobY = 0;
    this._squashAmt = 0;
    this._squashVel = 0;

    if (fbx) this._buildFromFbx();
  }

  buildMesh() {
    // Fallback procedural monolith
    const st = this.stats;
    const bodyMat = new THREE.MeshStandardMaterial({ color: st.bodyColor, roughness: 0.9 });
    const coreMat = new THREE.MeshStandardMaterial({
      color: st.accentColor,
      emissive: st.accentColor,
      emissiveIntensity: 1.2,
    });
    this._matBody = bodyMat;

    const base = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.4, 1.5), bodyMat);
    base.position.y = 0.7; base.castShadow = true; this.group.add(base);

    const torso = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.2, 1.2), bodyMat);
    torso.position.y = 2.0; torso.castShadow = true; this.group.add(torso);

    const core = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.3), coreMat);
    core.position.set(0, 2.1, 0.62); this.group.add(core);
    this._core = core;

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.6, 0.9), bodyMat);
    head.position.y = 2.9; head.castShadow = true; this.group.add(head);

    const armL = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.4, 0.45), bodyMat);
    armL.position.set(-1.0, 2.0, 0); armL.castShadow = true; this.group.add(armL);
    const armR = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.4, 0.45), bodyMat);
    armR.position.set(1.0, 2.0, 0); armR.castShadow = true; this.group.add(armR);
  }

  _buildFromFbx() {
    // Skinned FBX rigs must be cloned with SkeletonUtils; Object3D.clone(true)
    // can break skin bindings and result in invisible meshes.
    const source = SkeletonUtils.clone(this.fbx);
    const model = prepareModel(source);
    let meshCount = 0;
    model.traverse?.((node) => {
      if (node?.isMesh) meshCount++;
    });
    if (meshCount === 0) {
      console.warn('[boss] FBX loaded but has no meshes, using procedural fallback.');
      return;
    }

    // Clear procedural children, add FBX
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3(); box.getSize(size);
    const finiteHeight = Number.isFinite(size.y) && size.y > 0.0001;
    if (!finiteHeight) {
      console.warn('[boss] FBX bounds invalid, using procedural fallback.');
      this.buildMesh();
      return;
    }
    const targetH = this.stats.height;
    const rawScale = targetH / size.y;
    const saneScale = Number.isFinite(rawScale) ? THREE.MathUtils.clamp(rawScale, 0.0005, 50) : 1;
    model.scale.setScalar(saneScale * (this.phase === 2 ? 1.1 : 1.0));

    // Floor-align on Y.
    const scaledBox = new THREE.Box3().setFromObject(model);
    const floorY = Number.isFinite(scaledBox.min.y) ? scaledBox.min.y : 0;
    model.position.y -= floorY;

    // Phase 2 FBX uses a pivot that sits offset from gameplay origin, so use a
    // tighter recenter threshold there. Keep phase 1 behavior unchanged.
    const center = new THREE.Vector3();
    scaledBox.getCenter(center);
    const recenterThreshold = this.phase === 2 ? 0.2 : 4;
    if (Math.abs(center.x) > recenterThreshold || Math.abs(center.z) > recenterThreshold) {
      model.position.x -= center.x;
      model.position.z -= center.z;
    }
    this.group.add(model);
    this._model = model;
    this.usingFbxVisual = true;
    // Keep hit-flash material reference to first mesh found
    model.traverse((c) => { if (c.isMesh && !this._matBody) this._matBody = Array.isArray(c.material) ? c.material[0] : c.material; });

    if (this.fbx.animations && this.fbx.animations.length) {
      this.mixer = new THREE.AnimationMixer(model);
      for (const clip of this.fbx.animations) {
        const action = this.mixer.clipAction(clip);
        action.loop = THREE.LoopRepeat;
        this._actions[clip.name.toLowerCase()] = action;
      }

      this._idleAction = this._findAction(['idle']);
      this._walkAction = this._findAction(['walk']);
      this._aimAction = this._findAction(['aim']);
      this._jumpAction = this._findAction(['jump']);
      this._slashAction = this._findAction(['slash']);
      this._chargeAction = this._findAction(['charge']);
      this._powerAction = this._findAction(['powerup']);

      for (const action of [this._jumpAction, this._slashAction, this._powerAction]) {
        if (!action) continue;
        action.loop = THREE.LoopOnce;
        action.clampWhenFinished = true;
      }

      const first = this._idleAction || Object.values(this._actions)[0];
      if (first) { first.play(); this._currentAction = first; }
    }
  }

  _findAction(candidates) {
    for (const n of candidates) {
      for (const k of Object.keys(this._actions)) if (k.includes(n)) return this._actions[k];
    }
    return null;
  }

  _playAction(action, fade = 0.2) {
    if (!action || this._currentAction === action) return;
    if (this._currentAction) this._currentAction.fadeOut(fade);
    action.reset().fadeIn(fade).play();
    this._currentAction = action;
  }

  _playOneShot(action, onDone) {
    if (!action || !this.mixer) {
      if (onDone) onDone();
      return;
    }
    action.reset();
    action.loop = THREE.LoopOnce;
    action.clampWhenFinished = true;
    if (this._currentAction && this._currentAction !== action) {
      this._currentAction.fadeOut(0.12);
    }
    action.fadeIn(0.1).play();
    this._currentAction = action;
    const mixer = this.mixer;
    const handler = (e) => {
      if (e.action !== action) return;
      mixer.removeEventListener('finished', handler);
      if (onDone) onDone();
    };
    mixer.addEventListener('finished', handler);
  }

  _setBossState(state) {
    if (this._bossState === state) return;
    this._bossState = state;
    if (state === 'idle') this._playAction(this._idleAction || this._walkAction);
    else if (state === 'walk') this._playAction(this._walkAction || this._idleAction);
    else if (state === 'aim') this._playAction(this._aimAction || this._idleAction || this._walkAction);
  }

  updateHitFlash(dt) {
    if (this._core) {
      const p = this.hp / this.maxHp;
      this._core.material.emissiveIntensity = 1.2 + (1 - p) * 1.6;
    }
    super.updateHitFlash(dt);
  }

  updateAI(dt, game) {
    const player = game.player;
    if (!player.alive) return;
    if (this.mixer) this.mixer.update(dt);
    if (game?._bossPhase2SummonLock && !this._isSummoningHidden) {
      // Hard global lock: prevents any boss instance (including accidental
      // duplicates in sandbox) from entering attack logic while summon phase runs.
      this._movePattern = null;
      this._moveStep = 0;
      this._stepTimer = 0;
      this._aimDelay = 0;
      this._slashActive = false;
      this._pendingMelee = -1;
      this.fireCd = Math.max(this.fireCd, this.stats.fireCooldown * 2.5);
      this._setBossState('idle');
      return;
    }
    this._updateSummonPhase(game);
    if (this._isSummoningHidden) {
      this._awaitSummonAddsDefeated(game);
      return;
    }

    if (game?.isPlayerHiddenFromEnemies?.()) {
      this._movePattern = null;
      this._moveStep = 0;
      this._stepTimer = 0;
      this._aimDelay = 0;
      this._slashActive = false;
      this._pendingMelee = -1;
      this._setBossState('idle');
      return;
    }

    this._jumpCd = Math.max(0, this._jumpCd - dt);

    // Enrage under 40% for phase 2 — swap ENRAGED toast for the power-up one-shot.
    if (this.phase === 2 && !this.enraged && this.hp / this.maxHp < 0.4) {
      this.enraged = true;
      game.toast('ENRAGED');
      game.cameraRig.shake(1.0);
      if (this._powerAction) {
        this._powerupActive = true;
        this._bossState = 'powerup';
        this._playOneShot(this._powerAction, () => {
          this._powerupActive = false;
          this._bossState = 'idle';
          this._playAction(this._idleAction || this._walkAction);
        });
      }
    }

    // Compute aim vector once — we may early-return for special states.
    this.aimX = player.x - this.x;
    this.aimZ = player.z - this.z;
    const aimLen = Math.hypot(this.aimX, this.aimZ) || 1;
    this.aimX /= aimLen; this.aimZ /= aimLen;
    const d = aimLen;

    // Enrage power-up pulses freeze the boss in place.
    if (this._powerupActive) {
      return;
    }

    // Active jump-back tween — interpolate position, no other logic runs.
    if (this._jumpActive) {
      this._jumpT += dt;
      const t = Math.min(1, this._jumpDur > 0 ? this._jumpT / this._jumpDur : 1);
      const u = 1 - (1 - t) * (1 - t);
      this.x = this._jumpFromX + (this._jumpToX - this._jumpFromX) * u;
      this.z = this._jumpFromZ + (this._jumpToZ - this._jumpFromZ) * u;
      // Y parabola arc — peaks at t=0.5
      this._jumpArcT = 4 * t * (1 - t);
      this._jumpY = this._jumpArcT * 1.6;
      if (t >= 1) {
        this._jumpActive = false;
        this._jumpY = 0;
        this._jumpArcT = 0;
        // Kick the squash spring on landing
        this._squashAmt = 0.42;
        this._squashVel = 0;
        this._jumpCd = JUMP_BACK_COOLDOWN;
        this._bossState = 'idle';
        this._playAction(this._idleAction || this._walkAction);
      }
      return;
    }

    // Reactive jump-back: player too close, not mid-attack, cooldown ready.
    if (
      d < JUMP_BACK_TRIGGER
      && this._jumpCd <= 0
      && !this._movePattern
      && !this._slashActive
    ) {
      // Procedural boss (no FBX jump clip) still does a physical jump-back arc.
      const clipDur = (this._jumpAction?.getClip?.()?.duration) || 0.48;
      const awayX = -this.aimX;
      const awayZ = -this.aimZ;
      const targetX = this.x + awayX * JUMP_BACK_DISTANCE;
      const targetZ = this.z + awayZ * JUMP_BACK_DISTANCE;
      const resolved = resolveCircleWalls(targetX, targetZ, this.radius, game.room.walls);
      this._jumpFromX = this.x; this._jumpFromZ = this.z;
      this._jumpToX = resolved.x; this._jumpToZ = resolved.z;
      this._jumpDur = clipDur;
      this._jumpT = 0;
      this._jumpActive = true;
      this._physVx = 0; this._physVz = 0;
      this._bobY = 0;
      this._bossState = 'jumpBack';
      if (this._jumpAction) this._playOneShot(this._jumpAction, null);
      return;
    }

    // Slash one-shot active — boss is rooted, but we still tick the melee hit window.
    if (this._slashActive) {
      if (this._pendingMelee >= 0) {
        this._pendingMelee -= dt;
        if (this._pendingMelee <= 0 && !this._slashDealt) {
          this._slashDealt = true;
          const dx = player.x - this.x;
          const dz = player.z - this.z;
          if (Math.hypot(dx, dz) <= SLASH_CONNECT_RANGE && player.takeDamage) {
            player.takeDamage(this.stats.projectileDamage, game);
          }
        }
      }
      return;
    }

    const speedMul = this.enraged ? 1.35 : 1.0;

    // Capture previous position so we can derive velocity for the idle/walk pick.
    const prevX = this.x;
    const prevZ = this.z;

    // Velocity-based movement with inertia.
    // Each frame we compute a desired velocity and exponentially steer toward it,
    // giving the boss momentum and weight rather than instant direction changes.
    const maxSpd = this.stats.moveSpeed * speedMul;
    const STEER = 3.6; // lower = heavier inertia / more drift
    let desVX = 0, desVZ = 0;
    if (d > 14) {
      // Accelerate toward player
      desVX = this.aimX * maxSpd;
      desVZ = this.aimZ * maxSpd;
    } else if (d < 7) {
      // Back away from player
      desVX = -this.aimX * maxSpd;
      desVZ = -this.aimZ * maxSpd;
    } else {
      // Strafe perpendicular to aim with a sine oscillation
      const driftAmt = Math.sin(this.stateTime * 0.6);
      const px = -this.aimZ, pz = this.aimX;
      desVX = px * driftAmt * maxSpd * 0.5;
      desVZ = pz * driftAmt * maxSpd * 0.5;
    }
    const blend = 1 - Math.exp(-STEER * dt);
    this._physVx += (desVX - this._physVx) * blend;
    this._physVz += (desVZ - this._physVz) * blend;
    const movedPos = resolveCircleWalls(
      this.x + this._physVx * dt,
      this.z + this._physVz * dt,
      this.radius, game.room.walls
    );
    this.x = movedPos.x; this.z = movedPos.z;

    // Derive actual movement speed post-resolution for animation pick.
    const moveSpeed = dt > 0 ? Math.hypot(this.x - prevX, this.z - prevZ) / dt : 0;

    // Realign aim after movement mutates it.
    this.aimX = player.x - this.x;
    this.aimZ = player.z - this.z;
    const a2 = Math.hypot(this.aimX, this.aimZ) || 1;
    this.aimX /= a2; this.aimZ /= a2;

    // Sinusoidal body bob scaled by current movement speed — makes the boss
    // feel like it has mass and a physical gait when closing distance.
    if (!this._slashActive && !this._powerupActive) {
      const currentSpd = Math.hypot(this._physVx, this._physVz);
      const bobNorm = THREE.MathUtils.clamp(currentSpd / Math.max(0.1, maxSpd), 0, 1);
      this._bobY = Math.sin(this.stateTime * 7.0) * 0.055 * bobNorm;
    } else {
      this._bobY = 0;
    }

    // Attack pattern sequencing.
    if (this._movePattern) {
      this._executePattern(dt, game);
    } else if (this.fireCd <= 0) {
      this._movePattern = this.moves[this._moveIndex % this.moves.length];
      this._moveIndex++;
      this._moveStep = 0;
      this._stepTimer = 0;
      this._aimDelay = RANGED_PATTERNS.has(this._movePattern) ? AIM_TELEGRAPH : 0;
    }

    // Pick animation state for non-special frames. Slash/jump/powerup have already
    // returned above, so we only arbitrate between aim/walk/idle here.
    if (!this._slashActive && !this._jumpActive && !this._powerupActive) {
      if (this._movePattern && this._aimDelay > 0) {
        this._setBossState('aim');
      } else if (moveSpeed > 0.4) {
        this._setBossState('walk');
      } else {
        this._setBossState('idle');
      }
    }

    // Preserves the original double-decrement cadence (BaseEnemy.update also ticks fireCd).
    this.fireCd = Math.max(0, this.fireCd - dt);
  }

  _updateSummonPhase(game) {
    if (this.phase !== 2 || !this.alive || this.maxHp <= 0) {
      this._lastHpRatio = this.maxHp > 0 ? this.hp / this.maxHp : 1;
      return;
    }
    const hpRatio = Math.max(0, this.hp / this.maxHp);
    const crossed70 = !this._phase70Triggered && this._lastHpRatio > 0.7 && hpRatio <= 0.7;
    const crossed30 = !this._phase30Triggered && this._lastHpRatio > 0.3 && hpRatio <= 0.3;
    if (crossed70) {
      this._phase70Triggered = true;
      if (!this._phase2CardTriggered) {
        this._phase2CardTriggered = true;
        game?.tutorialRuntime?.triggerBossPhase2Card?.();
      }
      this._triggerSummonWave(game, [
        { type: 'enforcer', count: 5 },
        { type: 'zilua', count: 3 },
      ]);
    } else if (crossed30) {
      this._phase30Triggered = true;
      this._triggerSummonWave(game, [
        { type: 'enforcer', count: 6 },
        { type: 'zilua', count: 5 },
        { type: 'zealot', count: 3 },
      ]);
    }
    this._lastHpRatio = hpRatio;
  }

  _triggerSummonWave(game, composition) {
    if (!Array.isArray(composition) || composition.length === 0) return;
    if (this.phase === 2) game._bossPhase2SummonLock = true;
    this._enterSummonHiddenState(game);
    const spawned = [];
    this._pendingSummonDrops = 0;
    this._activeSummonWaveToken = `boss-${Date.now()}-${Math.floor((game.rng?.() || Math.random()) * 100000)}`;
    const waveToken = this._activeSummonWaveToken;
    for (const unit of composition) {
      const count = Math.max(0, unit?.count | 0);
      for (let i = 0; i < count; i++) {
        const pos = game._findSafeSummonSpawn(unit.type, this.x, this.z, {
          attempts: 40,
          minRadius: 2.6,
          maxRadius: 13,
          minPlayerDistance: 3.5,
          ignoreEnemy: this,
        }) || game._findSafeSummonSpawn(unit.type, this.x, this.z, {
          attempts: 16,
          minRadius: 1.2,
          maxRadius: 15,
          minPlayerDistance: 1.5,
          ignoreEnemy: this,
        });
        if (!pos) continue;
        this._pendingSummonDrops += 1;
        const delayS = 1.2 + (game.rng?.() || Math.random()) * 0.45;
        game.enemyDropPreview?.spawnAt(pos.x, pos.z, {
          duration: delayS,
          radius: 1.5,
          dropHeight: 14,
          onComplete: () => {
            this._pendingSummonDrops = Math.max(0, this._pendingSummonDrops - 1);
            if (!this.alive) return;
            const enemy = game._addEnemyByType(unit.type, pos.x, pos.z);
            if (enemy?.alive) {
              enemy._bossSummonWaveToken = waveToken;
              spawned.push(enemy);
            }
          },
        });
      }
    }
    this._activeSummonAdds = spawned;
    game.toast('BOSS SUMMON');
    game.cameraRig?.shake?.(0.9);
  }

  _computeSummonParkPosition(game) {
    const player = game?.player;
    if (!player?.alive) return { x: this.x, z: this.z };

    // "Seberang player": mirror current boss direction through player,
    // then clamp to a safe walkable spawn so it stays inside the map.
    let awayX = this.x - player.x;
    let awayZ = this.z - player.z;
    let len = Math.hypot(awayX, awayZ);
    if (len < 0.001) {
      awayX = -(player.aimX || 0);
      awayZ = -(player.aimZ || 1);
      len = Math.hypot(awayX, awayZ) || 1;
    }
    awayX /= len;
    awayZ /= len;

    const targetDist = Math.max(6.5, Math.min(10.5, len));
    const rawX = player.x - awayX * targetDist;
    const rawZ = player.z - awayZ * targetDist;
    const parked = game?._findSafeSummonSpawn?.('boss', rawX, rawZ, {
      attempts: 42,
      minRadius: 0.25,
      maxRadius: 3.4,
      minPlayerDistance: 0,
      ignoreEnemy: this,
    });
    if (parked) return parked;
    return { x: this.x, z: this.z };
  }

  _enterSummonHiddenState(game) {
    if (this._isSummoningHidden) return;
    this._summonReturnX = this.x;
    this._summonReturnZ = this.z;
    this._isSummoningHidden = true;
    this._bossState = 'idle';
    this._movePattern = null;
    this._moveStep = 0;
    this._stepTimer = 0;
    this._aimDelay = 0;
    this._jumpActive = false;
    this._slashActive = false;
    this._pendingMelee = -1;
    this._powerupActive = false;
    this.fireCd = Math.max(this.fireCd, this.stats.fireCooldown * 2.5);
    this._physVx = 0;
    this._physVz = 0;
    this.group.visible = false;
    if (this.phase === 2) game._bossPhase2SummonLock = true;
    if (this._hiddenRadius == null) this._hiddenRadius = this.radius;
    this.radius = 0.01;
    const parked = this._computeSummonParkPosition(game);
    this._summonParkX = parked.x;
    this._summonParkZ = parked.z;
    this._clearActiveBossProjectiles(game);
    if (this._currentAction?.stop) this._currentAction.stop();
  }

  _clearActiveBossProjectiles(game) {
    const pool = game?.projectiles;
    if (!pool?.forEach || !pool?.release) return;
    pool.forEach((p) => {
      if (!p?.alive) return;
      if (p.team !== 1) return;
      if (!p.isBossShot) return;
      p.alive = false;
      p.life = 0;
      if (p.mesh) p.mesh.visible = false;
      pool.release(p);
    });
  }

  _awaitSummonAddsDefeated(game) {
    this.group.visible = false;
    this._activeSummonAdds = this._activeSummonAdds.filter((e) => e?.alive);
    const aliveTrackedAdds = this._activeSummonWaveToken
      ? (game?.enemies || []).filter((e) => e?.alive && e._bossSummonWaveToken === this._activeSummonWaveToken).length
      : 0;
    if (this._pendingSummonDrops > 0 || this._activeSummonAdds.length > 0 || aliveTrackedAdds > 0) return;
    this._isSummoningHidden = false;
    if (this.phase === 2) game._bossPhase2SummonLock = false;
    let resumeX = Number.isFinite(this._summonReturnX) ? this._summonReturnX : this.x;
    let resumeZ = Number.isFinite(this._summonReturnZ) ? this._summonReturnZ : this.z;
    const clearResume = game?._isSpawnPointClear?.(resumeX, resumeZ, this._hiddenRadius ?? this.radius, {
      minPlayerDistance: 0,
      ignoreEnemy: this,
    });
    if (!clearResume) {
      const safe = game?._findSafeSummonSpawn?.('boss', resumeX, resumeZ, {
        attempts: 28,
        minRadius: 0.2,
        maxRadius: 4.2,
        minPlayerDistance: 0,
        ignoreEnemy: this,
      });
      if (safe) {
        resumeX = safe.x;
        resumeZ = safe.z;
      }
    }
    this.x = resumeX;
    this.z = resumeZ;
    this.group.visible = true;
    if (this._hiddenRadius != null) {
      this.radius = this._hiddenRadius;
      this._hiddenRadius = null;
    }
    this._summonParkX = null;
    this._summonParkZ = null;
    this._summonReturnX = null;
    this._summonReturnZ = null;
    this._activeSummonWaveToken = null;
    this._setBossState('idle');
    this._playAction(this._idleAction || this._walkAction);
    game.toast('BOSS RETURNS');
  }

  _applySummonHiddenVisualState() {
    const hideX = Number.isFinite(this._summonParkX) ? this._summonParkX : this.x;
    const hideZ = Number.isFinite(this._summonParkZ) ? this._summonParkZ : this.z;
    this.x = hideX;
    this.z = hideZ;
    this.group.visible = false;
    this.group.position.set(hideX, -1000, hideZ);
    this.group.scale.set(1, 1, 1);
  }

  _executePattern(dt, game) {
    if (this._isSummoningHidden || game?._bossPhase2SummonLock) {
      this._movePattern = null;
      this.fireCd = Math.max(this.fireCd, this.stats.fireCooldown * 2.5);
      this._setBossState('idle');
      return;
    }
    const pat = this._movePattern;

    // Pre-fire "aim" telegraph. Ranged patterns stall briefly so the bow-draw reads.
    if (this._aimDelay > 0) {
      this._aimDelay -= dt;
      if (this._aimDelay > 0) return;
    }

    // Slash is a melee move: play the one-shot, schedule a hit window, then finish.
    if (pat === 'slash') {
      const player = game.player;
      const dx = player.x - this.x;
      const dz = player.z - this.z;
      const d = Math.hypot(dx, dz);
      if (d > SLASH_RANGE + 0.6) {
        // Player slipped out of melee range — skip slash, queue the next move soon.
        this._movePattern = null;
        this.fireCd = Math.min(this.fireCd, this.stats.fireCooldown * 0.4);
        return;
      }
      if (!this._slashAction) {
        if (player.takeDamage) player.takeDamage(this.stats.projectileDamage, game);
        this._movePattern = null;
        this.fireCd = this.stats.fireCooldown;
        return;
      }
      const clipDur = (this._slashAction.getClip?.()?.duration) || 0.8;
      this._slashActive = true;
      this._slashDealt = false;
      this._pendingMelee = clipDur * 0.4;
      this._bossState = 'slash';
      if (this.audio) this.audio.play('bossShot', { pitchJitter: 0.05 });
      this._playOneShot(this._slashAction, () => {
        this._slashActive = false;
        this._pendingMelee = -1;
        this._movePattern = null;
        this.fireCd = this.stats.fireCooldown;
        this._bossState = 'idle';
        this._playAction(this._idleAction || this._walkAction);
      });
      return;
    }

    this._stepTimer -= dt;
    const firePattern = () => {
      if (this._isSummoningHidden || game?._bossPhase2SummonLock) {
        this._movePattern = null;
        this.fireCd = Math.max(this.fireCd, this.stats.fireCooldown * 2.5);
        this._setBossState('idle');
        return;
      }
      const speed = this.stats.projectileSpeed;
      const dmg = this.stats.projectileDamage;
      const muzzleX = this.x + this.aimX * 1.2;
      const muzzleZ = this.z + this.aimZ * 1.2;
      if (this.audio) this.audio.play('bossShot', { pitchJitter: 0.02 });
      if (pat === 'single') {
        game.spawnEnemyProjectile(muzzleX, muzzleZ, this.aimX, this.aimZ, { speed, damage: dmg, life: 2, boss: true, sfx: false });
        game.onBossFire(this);
        this._physVx -= this.aimX * 0.8; this._physVz -= this.aimZ * 0.8;
        this._squashAmt = Math.max(this._squashAmt, 0.08);
        this._movePattern = null; this.fireCd = this.stats.fireCooldown;
      } else if (pat === 'fan3' || pat === 'fan5') {
        const n = pat === 'fan3' ? 3 : 5;
        const spread = pat === 'fan3' ? 0.25 : 0.45;
        const baseAng = Math.atan2(this.aimX, this.aimZ);
        for (let i = 0; i < n; i++) {
          const t = n === 1 ? 0.5 : i / (n - 1);
          const ang = baseAng + (t - 0.5) * spread * 2;
          const dx = Math.sin(ang), dz = Math.cos(ang);
          game.spawnEnemyProjectile(muzzleX, muzzleZ, dx, dz, { speed, damage: dmg, life: 2, boss: true, sfx: false });
        }
        // Wider fans hit harder.
        game.onBossFire(this, { intensityScale: pat === 'fan5' ? 1.5 : 1.2 });
        // Bigger spread = bigger recoil kick
        const fanRecoil = pat === 'fan5' ? 1.6 : 1.0;
        this._physVx -= this.aimX * fanRecoil; this._physVz -= this.aimZ * fanRecoil;
        this._squashAmt = Math.max(this._squashAmt, pat === 'fan5' ? 0.12 : 0.1);
        this._movePattern = null; this.fireCd = this.stats.fireCooldown;
      } else if (pat === 'spiral') {
        const baseAng = this.stateTime * 4;
        const arms = 3;
        for (let i = 0; i < arms; i++) {
          const ang = baseAng + (i * Math.PI * 2) / arms;
          const dx = Math.sin(ang), dz = Math.cos(ang);
          game.spawnEnemyProjectile(muzzleX, muzzleZ, dx, dz, { speed: speed * 0.8, damage: dmg, life: 2.5, boss: true, sfx: false });
        }
        // Subtle rolling shake on each spiral tick.
        game.onBossFire(this, { intensityScale: 0.6 });
        // Small per-tick nudge creates a subtle wobble during the spiral
        this._physVx -= this.aimX * 0.3; this._physVz -= this.aimZ * 0.3;
        this._squashAmt = Math.max(this._squashAmt, 0.05);
        this._moveStep++;
        this._stepTimer = 0.08;
        if (this._moveStep >= 14) { this._movePattern = null; this.fireCd = this.stats.fireCooldown; }
      } else if (pat === 'chargeBurst') {
        // 7 round burst at player
        const jitter = 0.14;
        for (let i = 0; i < 2; i++) {
          const ang = Math.atan2(this.aimX, this.aimZ) + (Math.random() * 2 - 1) * jitter;
          const dx = Math.sin(ang), dz = Math.cos(ang);
          game.spawnEnemyProjectile(muzzleX, muzzleZ, dx, dz, { speed: speed * 1.1, damage: dmg, life: 2, boss: true, sfx: false });
        }
        // Each burst pulse stacks up — feels like a machinegun rumble.
        game.onBossFire(this, { intensityScale: 0.75 });
        // Stacked micro-recoil across the burst
        this._physVx -= this.aimX * 0.5; this._physVz -= this.aimZ * 0.5;
        this._squashAmt = Math.max(this._squashAmt, 0.07);
        this._moveStep++;
        this._stepTimer = 0.12;
        if (this._moveStep >= 7) { this._movePattern = null; this.fireCd = this.stats.fireCooldown; }
      } else {
        this._movePattern = null; this.fireCd = this.stats.fireCooldown;
      }
    };

    if (this._stepTimer <= 0) {
      firePattern();
    }
  }

  // Override BaseEnemy.update to layer physics-driven Y position and squash/stretch
  // on top of the base XZ position logic.
  update(dt, game, options = {}) {
    if (this._isSummoningHidden) {
      if (this.mixer) this.mixer.update(dt);
      this._awaitSummonAddsDefeated(game);
      if (!this._isSummoningHidden) {
        // Summon phase ended this frame; resume normal boss update immediately.
        super.update(dt, game, options);
        return;
      }
      this._clearActiveBossProjectiles(game);
      this._applySummonHiddenVisualState();
      return;
    }
    super.update(dt, game, options);
    if (this._isSummoningHidden) {
      // Threshold can flip hidden state inside super.update -> updateAI.
      // Force-hide immediately in the same frame so boss never lingers visually.
      this._applySummonHiddenVisualState();
      return;
    }

    // Squash/stretch spring — critically damped, kicks in on jump landing.
    if (this._squashAmt !== 0 || this._squashVel !== 0) {
      const squashAcc = -22 * this._squashAmt - 7 * this._squashVel;
      this._squashVel += squashAcc * dt;
      this._squashAmt += this._squashVel * dt;
      if (Math.abs(this._squashAmt) < 0.001 && Math.abs(this._squashVel) < 0.005) {
        this._squashAmt = 0; this._squashVel = 0;
      }
    }

    // BaseEnemy hard-codes group.position.y = 0; override it with our arc + bob.
    this.group.position.y = this._jumpY + this._bobY;

    // Vertical scale: slight stretch at jump apex, squash on landing, neutral otherwise.
    const yScale = Math.max(0.35, 1.0 + this._jumpArcT * 0.07 - this._squashAmt);
    const xzScale = 1 / Math.sqrt(Math.max(0.001, yScale));
    if (Math.abs(yScale - 1) > 0.003 || Math.abs(xzScale - 1) > 0.003) {
      this.group.scale.set(xzScale, yScale, xzScale);
    } else {
      this.group.scale.set(1, 1, 1);
    }
  }

  // Override BaseEnemy.takeDamage to apply a velocity knockback impulse.
  takeDamage(n, game) {
    if (this._isSummoningHidden) return;
    super.takeDamage(n, game);
    if (!this.alive) return;
    // Use queued hit direction if available (set by queueHitDirection before this call),
    // otherwise fall back to pushing away from the player's position.
    let kx = this._pendingHitDx ?? -this.aimX;
    let kz = this._pendingHitDz ?? -this.aimZ;
    this._pendingHitDx = null;
    this._pendingHitDz = null;
    if (!Number.isFinite(kx) || !Number.isFinite(kz)) { kx = 0; kz = 0; }
    const impulse = this.enraged ? 2.5 : 3.5;
    this._physVx += kx * impulse;
    this._physVz += kz * impulse;
    this._squashAmt = Math.max(this._squashAmt, 0.16);
  }
}
